import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  NgZone,
  Input,
} from "@angular/core";
import { Subject, fromEvent } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Directive({
  selector: "[columnResize]",
})
export class ColumnResizeDirective implements OnInit, OnDestroy {
  @Input() resizableTable: HTMLElement | null = null;
  @Input() fixedIndex?: number;

  private isResizing = false;
  private startX!: number;
  private startWidth!: number;
  private column: HTMLElement;
  private table: HTMLElement | null = null;
  private resizer!: HTMLElement;
  private destroy = new Subject<void>();

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private zone: NgZone
  ) {
    this.column = this.el.nativeElement;
  }

  ngOnInit() {
    this.table = this.resizableTable || this.findParentTable(this.column);

    if (!this.table) {
      return;
    }

    this.createResizer();
    this.initializeResizeListener();
  }

  private createResizer() {
    this.resizer = this.renderer.createElement("div");
    this.renderer.addClass(this.resizer, "column-resizer");
    this.renderer.setStyle(this.resizer, "position", "absolute");
    this.renderer.setStyle(this.resizer, "right", "0");
    this.renderer.setStyle(this.resizer, "top", "0");
    this.renderer.setStyle(this.resizer, "height", "100%");
    this.renderer.setStyle(this.resizer, "width", "5px");
    this.renderer.setStyle(this.resizer, "cursor", "col-resize");
    // Ensure resizer is above other elements
    this.renderer.setStyle(this.resizer, "z-index", "10");
    this.renderer.appendChild(this.column, this.resizer);
  }

  private initializeResizeListener() {
    this.zone.runOutsideAngular(() => {
      // Prevent sort on mousedown and stop propagation
      fromEvent(this.resizer, "mousedown")
        .pipe(takeUntil(this.destroy))
        .subscribe((e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.onMouseDown(e as MouseEvent);
        });

      // prevent click events on the resizer
      fromEvent(this.resizer, "click")
        .pipe(takeUntil(this.destroy))
        .subscribe((e: Event) => {
          e.preventDefault();
          e.stopPropagation();
        });

      fromEvent(document, "mousemove")
        .pipe(takeUntil(this.destroy))
        .subscribe((e: Event) => this.onMouseMove(e as MouseEvent));

      fromEvent(document, "mouseup")
        .pipe(takeUntil(this.destroy))
        .subscribe(() => this.onMouseUp());
    });
  }

  private onMouseDown(e: MouseEvent) {
    e.preventDefault();
    // Prevent event from reaching sort header
    e.stopPropagation();

    this.isResizing = true;
    this.startX = e.pageX;
    this.startWidth = this.column.offsetWidth;
    this.renderer.addClass(this.column, "resizing");
    if (this.table) {
      this.renderer.addClass(this.table, "resizing");
    }
  }

  private onMouseMove(e: MouseEvent) {
    if (!this.isResizing) return;
    e.preventDefault();

    const width = this.startWidth + (e.pageX - this.startX);
    this.renderer.setStyle(this.column, "width", `${width}px`);
    this.setColumnWidth(width);
  }

  private onMouseUp() {
    if (!this.isResizing) return;
    this.isResizing = false;
    this.renderer.removeClass(this.column, "resizing");
    if (this.table) {
      this.renderer.removeClass(this.table, "resizing");
    }
  }

  private findParentTable(element: HTMLElement): HTMLElement | null {
    while (element && element.tagName !== "TABLE") {
      element = element.parentElement as HTMLElement;
      if (!element) return null;
    }
    return element;
  }

  private setColumnWidth(width: number) {
    this.renderer.setStyle(this.el.nativeElement, "width", `${width}px`);

    if (this.fixedIndex && this.fixedIndex <= 3) {
      const varName = `--fixed-column-${this.fixedIndex}-width`;
      if (this.table) {
        this.table.style.setProperty(varName, `${width}px`);
      }
    }
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }
}
